import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box, Button, ButtonGroup, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Select,
  Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import SearchIcon from '@mui/icons-material/Search'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import { motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { User, Role, Team, Site } from '@cmms/types'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tooltip from '@mui/material/Tooltip'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'

import { UserAvatar } from '../../components/UserAvatar'
import { usePermissions } from '../../hooks/usePermissions'
import { CreateUserDialog } from './CreateUserDialog'
import { UsersImportDialog } from './UsersImportDialog'
import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { MemberList } from './components/MemberList'
import { MemberDetail } from './components/MemberDetail'
import { SearchableSelect } from '../../components/SearchableSelect'
import { TeamsView } from './components/TeamsView'
import { InvitationsList } from './components/InvitationsList'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'

type TabKey = 'members' | 'teams' | 'invites'
type MemberKind = 'internal' | 'client' | 'vendor'

// Bucket membership by flag rather than mutual-exclusion — Client and
// Vendor are independent tags, so Internal is "neither flag set"
// (avoids double-counting a user flagged both into Internal too).
function inBucket(u: User, kind: MemberKind): boolean {
  if (kind === 'client') return u.is_client
  if (kind === 'vendor') return u.is_vendor
  return !u.is_client && !u.is_vendor
}

export function PeoplePage() {
  const navigate = useNavigate()
  const { userId: urlUserId } = useParams<{ userId?: string }>()
  const [tab, setTab]           = useState<TabKey>('members')
  const [memberKind, setMemberKind] = useState<MemberKind>('internal')
  const [selectedId, setSelectedId] = useState<string | null>(urlUserId ?? null)
  useEffect(() => { if (urlUserId) setSelectedId(urlUserId) }, [urlUserId])
  const [search, setSearch]     = useState('')
  const [roleFilter, setRoleFilter]     = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [tagFilter, setTagFilter]       = useState<string>('all')
  const [inviteOpen, setInviteOpen]     = useState(false)
  const [createUserOpen, setCreateUserOpen] = useState(false)
  const [csvImportOpen, setCsvImportOpen]   = useState(false)
  const [addMenuAnchor, setAddMenuAnchor]   = useState<HTMLElement | null>(null)
  const { can } = usePermissions()
  const [createTeamOpen, setCreateTeamOpen] = useState(false)

  // ─── Queries ─────────────────────────────────────────────────────
  // `q` matches name/email directly, or any customer the user has
  // access to (e.g. searching "AWFIS" surfaces everyone with access
  // to a site under that customer) — done server-side since customer
  // access isn't part of the User payload otherwise. Role/status stay
  // client-side filters below since the full (already-narrowed) list
  // is in hand either way.
  const trimmedSearch = search.trim()
  const usersQ = useQuery({
    queryKey: ['users', { q: trimmedSearch || undefined, tag_id: tagFilter !== 'all' ? tagFilter : undefined }],
    queryFn: () => api.users.list({
      q: trimmedSearch || undefined,
      tag_id: tagFilter !== 'all' ? tagFilter : undefined,
    }),
  })
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: () => api.tags.list(), staleTime: 5 * 60_000 })
  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.roles.list(),
  })
  // Pending invitations — used to show a count on the "Invites" tab and
  // power the invitations list when that tab is active. Only fetched if
  // the user can manage invitations (the endpoint would 403 anyway).
  const invitationsQ = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.invitations.list(),
    enabled: can('settings:manage_invitations'),
  })
  const pendingInviteCount = (invitationsQ.data ?? [])
    .filter((i) => i.status === 'pending').length

  const teamsQ = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.teams.list(),
  })
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60 * 1000 })

  // Defensive: API might return an object wrapper instead of a plain array
  const users: User[] = Array.isArray(usersQ.data) ? usersQ.data : []
  const roles: Role[] = Array.isArray(rolesQ.data) ? rolesQ.data : []
  const teams: Team[] = Array.isArray(teamsQ.data) ? teamsQ.data : []
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  // Users in the currently-visible bucket. `selected` and the auto-
  // select effect below both key off this so switching sub-tabs never
  // leaves a stale detail pane behind — a selection can only exist if
  // it lives in the bucket you're looking at. Internal = neither flag
  // set, so a user flagged both client and vendor doesn't get double-
  // counted into Internal too.
  const bucketUsers = useMemo(
    () => users.filter((u) => inBucket(u, memberKind)),
    [users, memberKind],
  )

  // If a user was pre-selected via URL and lives in a DIFFERENT bucket
  // (e.g. hitting /people/<clientId> while the sub-tab defaulted to
  // Internal), flip the tab to match once so the row is visible.
  useEffect(() => {
    if (!urlUserId) return
    const target = users.find((u) => u.id === urlUserId)
    if (!target) return
    setMemberKind(target.is_client ? 'client' : target.is_vendor ? 'vendor' : 'internal')
  }, [urlUserId, users])

  function handleSelectUser(id: string) {
    setSelectedId(id)
    navigate(`/people/${id}`, { replace: true })
  }

  // Build role options from actual roles in the DB
  const roleOptions = useMemo(() => {
    const uniqueRoles = [...new Set(users.map((u) => u.role))]
    return [
      { value: 'all', label: 'All roles' },
      ...uniqueRoles.map((r) => ({
        value: r,
        label: r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      })),
    ]
  }, [users])

  // Split members into internal / client / vendor buckets. Pure display
  // split — `u.is_client`/`u.is_vendor` are flags on the users table
  // with no permission side effects, so downstream views (assignment
  // pickers, teams, workflows) stay unchanged.
  const internalCount = useMemo(() => users.filter((u) => inBucket(u, 'internal')).length, [users])
  const clientCount   = useMemo(() => users.filter((u) => inBucket(u, 'client')).length, [users])
  const vendorCount   = useMemo(() => users.filter((u) => inBucket(u, 'vendor')).length, [users])

  // Filter users — name/email/customer search and tag are already
  // applied server-side (see usersQ above); only role/status narrow
  // the already-fetched list further here.
  const filtered = useMemo(() => {
    let list = users.filter((u) => inBucket(u, memberKind))
    if (roleFilter   !== 'all') list = list.filter((u) => u.role === roleFilter)
    if (statusFilter !== 'all') {
      list = list.filter((u) =>
        statusFilter === 'active' ? u.is_active : !u.is_active,
      )
    }
    return list
  }, [users, memberKind, roleFilter, statusFilter])

  // Selection has to come from `bucketUsers` (not the flat list) so a
  // previously-selected internal user doesn't render in the detail
  // pane while the Clients sub-tab is active, and vice versa.
  const selected = selectedId
    ? bucketUsers.find((u) => u.id === selectedId) ?? null
    : null

  // Keep the selection consistent with the visible bucket. Runs on
  // mount, on bucket switch, and whenever `users` refetches:
  //   • selection lives in the bucket → keep it (no-op)
  //   • otherwise + bucket has rows   → auto-select the first row
  //   • otherwise + bucket empty      → clear the URL id and selection
  useEffect(() => {
    if (selected) return
    const first = bucketUsers[0]
    if (first) {
      setSelectedId(first.id)
      navigate(`/people/${first.id}`, { replace: true })
    } else if (selectedId || urlUserId) {
      setSelectedId(null)
      navigate('/people', { replace: true })
    }
  }, [selected, bucketUsers, selectedId, urlUserId, navigate])

  const activeFilterCount =
    (roleFilter !== 'all' ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    (tagFilter !== 'all' ? 1 : 0)

  const tagOptions = useMemo(() => [
    { value: 'all', label: 'All tags' },
    ...(Array.isArray(tagsQ.data) ? tagsQ.data : []).map((t) => ({ value: t.id, label: t.name })),
  ], [tagsQ.data])
  const tagFilterLabel = tagFilter === 'all' ? 'Tag' : (tagOptions.find((o) => o.value === tagFilter)?.label ?? 'Tag')

  const isLoading = usersQ.isLoading || rolesQ.isLoading || teamsQ.isLoading

  const toolbar = (
    <Stack spacing={1.5}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36, textTransform: 'none',
            fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
            color: 'text.secondary', px: 0, mr: 3,
            '&.Mui-selected': { color: 'primary.main' },
          },
          '& .MuiTabs-indicator': { height: 2 },
        }}
      >
        <Tab
          value="members"
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <PersonOutlineIcon sx={{ fontSize: 16 }} />
              <span>Members</span>
              <Box component="span" sx={{
                fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                color: 'text.secondary', bgcolor: 'action.hover',
                px: 0.65, py: 0.15, borderRadius: 0.75,
              }}>
                {users.length}
              </Box>
            </Stack>
          }
        />
        <Tab
          value="teams"
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <GroupsOutlinedIcon sx={{ fontSize: 16 }} />
              <span>Teams</span>
              <Box component="span" sx={{
                fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                color: 'text.secondary', bgcolor: 'action.hover',
                px: 0.65, py: 0.15, borderRadius: 0.75,
              }}>
                {teams.length}
              </Box>
            </Stack>
          }
        />
        {can('settings:manage_invitations') && (
          <Tab
            value="invites"
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <MarkEmailReadOutlinedIcon sx={{ fontSize: 16 }} />
                <span>Invites</span>
                {pendingInviteCount > 0 && (
                  <Box component="span" sx={{
                    fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: 'text.secondary', bgcolor: 'action.hover',
                    px: 0.65, py: 0.15, borderRadius: 0.75,
                  }}>
                    {pendingInviteCount}
                  </Box>
                )}
              </Stack>
            }
          />
        )}
      </Tabs>

      {tab === 'members' && (
        <Stack spacing={1.25}>
          {/* Internal / Client / Vendor sub-tabs. Pure display split —
              the filter drives the list on this page only and doesn't
              touch teams, workflows, or any assignment picker downstream. */}
          <Stack direction="row" spacing={0.5} sx={{
            display: 'inline-flex', p: 0.4, borderRadius: 2,
            bgcolor: 'action.hover', alignSelf: 'flex-start',
          }}>
            {(['internal', 'client', 'vendor'] as const).map((k) => {
              const isActive = memberKind === k
              const count = k === 'internal' ? internalCount : k === 'client' ? clientCount : vendorCount
              return (
                <Box key={k}
                  onClick={() => setMemberKind(k)}
                  sx={{
                    cursor: 'pointer', px: 1.5, py: 0.55, borderRadius: 1.5,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    transition: 'background-color 120ms ease, color 120ms ease',
                    bgcolor: isActive ? 'background.paper' : 'transparent',
                    boxShadow: isActive ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
                    color: isActive ? 'text.primary' : 'text.secondary',
                    '&:hover': !isActive ? {
                      color: 'text.primary',
                    } : undefined,
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '-0.005em' }}>
                    {k === 'internal' ? 'Internal users' : k === 'client' ? 'Clients' : 'Vendors'}
                  </Typography>
                  <Box component="span" sx={{
                    fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    bgcolor: isActive ? (t) => `${t.palette.primary.main}14` : 'action.hover',
                    px: 0.65, py: 0.05, borderRadius: 0.75, minWidth: 18, textAlign: 'center',
                  }}>
                    {count}
                  </Box>
                </Box>
              )
            })}
          </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            placeholder="Search by name, email, or customer"
            value={search} onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{
              width: { xs: '100%', md: 300 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper', fontSize: 13, borderRadius: 2,
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'text.secondary' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ width: 1 }} />

          <FilterChip
            icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />}
            label={roleFilter === 'all' ? 'Role' : formatRole(roleFilter)}
            active={roleFilter !== 'all'}
            value={roleFilter}
            onChange={setRoleFilter}
            options={roleOptions}
          />
          <FilterChip
            icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
            label={statusFilter === 'all' ? 'Status' : (statusFilter === 'active' ? 'Active' : 'Inactive')}
            active={statusFilter !== 'all'}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all',      label: 'All' },
              { value: 'active',   label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
          <FilterChip
            icon={<LocalOfferOutlinedIcon sx={{ fontSize: 14 }} />}
            label={tagFilterLabel}
            active={tagFilter !== 'all'}
            value={tagFilter}
            onChange={setTagFilter}
            options={tagOptions}
          />

          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={() => { setRoleFilter('all'); setStatusFilter('all'); setTagFilter('all') }}
              startIcon={<TuneOutlinedIcon sx={{ fontSize: 14 }} />}
              sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0 }}
            >
              Clear ({activeFilterCount})
            </Button>
          )}
        </Stack>
        </Stack>
      )}
    </Stack>
  )

  return (
    <PageShell
      title="People"
      toolbar={toolbar}
      scrollContent={false}
      actions={
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 400, damping: 28 }}
        >
          {tab === 'teams' ? (
            <Tooltip title={can('teams:create') ? '' : "You don't have permission to create teams"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setCreateTeamOpen(true)}
                  disabled={!can('teams:create')}
                  sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em', borderRadius: 2,
                    boxShadow: primary.glow, '&:hover': { boxShadow: primary.glowHover } }}>
                  New team
                </Button>
              </Box>
            </Tooltip>
          ) : (
            // Split-button: primary = Invite (existing flow), dropdown
            // exposes the two new direct paths (admin sets credentials,
            // or CSV bulk import). Permission gate matches the primary.
            <Tooltip title={can('settings:manage_invitations') ? '' : "You don't have permission to add members"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <ButtonGroup variant="contained" disableElevation
                  disabled={!can('settings:manage_invitations')}
                  sx={{ borderRadius: 2, boxShadow: primary.glow, '&:hover': { boxShadow: primary.glowHover } }}>
                  <Button startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setInviteOpen(true)}
                    sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em' }}>
                    Invite member
                  </Button>
                  <Button size="small" onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                    sx={{ minWidth: 32, px: 0.5 }}>
                    <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                  </Button>
                </ButtonGroup>
                <Menu anchorEl={addMenuAnchor} open={!!addMenuAnchor}
                  onClose={() => setAddMenuAnchor(null)}
                  slotProps={{ paper: { sx: { borderRadius: 1.5, minWidth: 240, mt: 0.5 } } }}>
                  <MenuItem onClick={() => { setAddMenuAnchor(null); setInviteOpen(true) }} sx={{ fontSize: 13 }}>
                    <ListItemIcon><EmailOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Invite by email</Typography>}
                      secondary={<Typography sx={{ fontSize: 11, color: 'text.secondary' }}>They set their own password</Typography>} />
                  </MenuItem>
                  <MenuItem onClick={() => { setAddMenuAnchor(null); setCreateUserOpen(true) }} sx={{ fontSize: 13 }}>
                    <ListItemIcon><PersonAddOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Create user directly</Typography>}
                      secondary={<Typography sx={{ fontSize: 11, color: 'text.secondary' }}>You set the credentials and share them</Typography>} />
                  </MenuItem>
                  <MenuItem onClick={() => { setAddMenuAnchor(null); setCsvImportOpen(true) }} sx={{ fontSize: 13 }}>
                    <ListItemIcon><UploadFileOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Import from CSV</Typography>}
                      secondary={<Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Bulk-create users from a spreadsheet</Typography>} />
                  </MenuItem>
                </Menu>
              </Box>
            </Tooltip>
          )}
        </motion.div>
      }
    >
      <Box
        sx={{
          flex: 1, minHeight: 0,
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          border: '1px solid', borderColor: 'divider', borderRadius: 2,
          overflow: 'hidden', bgcolor: 'background.paper',
          mb: { xs: 3, md: 4 },
        }}
      >
        {isLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : tab === 'members' ? (
          <>
            <MemberList
              users={filtered}
              selectedId={selectedId}
              onSelect={handleSelectUser}
            />
            {selected && (
              <MemberDetail
                user={selected}
                roles={roles}
                teams={teams}
              />
            )}
          </>
        ) : tab === 'invites' ? (
          <InvitationsList />
        ) : (
          <TeamsView teams={teams} externalCreateOpen={createTeamOpen} onExternalCreateClose={() => setCreateTeamOpen(false)} />
        )}
      </Box>

      {/* Invite dialog (existing flow — sends email link) */}
      <InviteMemberDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        roles={roles}
      />
      {/* Direct-create dialog (admin sets the password) */}
      <CreateUserDialog
        open={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        roles={roles}
      />
      {/* CSV bulk-import dialog */}
      <UsersImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
      />
    </PageShell>
  )
}

// ─── Invite member dialog ──────────────────────────────────────────────────

function InviteMemberDialog({
  open, onClose, roles,
}: {
  open: boolean
  onClose: () => void
  roles: Role[]
}) {
  const qc = useQueryClient()
  const [email, setEmail]       = useState('')
  const [fullName, setFullName] = useState('')
  const [roleId, setRoleId]     = useState('')
  const [siteIds, setSiteIds]   = useState<string[]>([])
  const [allSites, setAllSites] = useState(false)
  // Multi-customer state — only used when the org has 2+ customers.
  const [customerIds, setCustomerIds] = useState<string[]>([])
  const [isClient, setIsClient] = useState(false)
  const [isVendor, setIsVendor] = useState(false)
  // Free-text filter for the customer + site picker. Matches customer
  // names AND descendant site names — see the CreateUserDialog copy
  // for the same behaviour.
  const [scopeQuery, setScopeQuery] = useState('')

  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60 * 1000,
  })
  const customers = Array.isArray(customersQ.data) ? customersQ.data : []

  // Use a dedicated cross-customer query — the page-level `sitesQ` is
  // narrowed by the active-customer switcher, which would hide sites
  // under sibling customers from the picker.
  const sitesQ = useQuery({
    queryKey: ['sites', 'all-customers'],
    queryFn: () => api.sites.list({ allCustomers: true }),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const selectedRole = roles.find((r) => r.id === roleId)
  const isAdmin = selectedRole?.name === 'org_admin'
  // customer_admin manages every site under its customers; the UI
  // hides the per-site picker in this case and the backend strips
  // any site_ids defensively.
  const isCustomerAdmin = selectedRole?.name === 'customer_admin'
  // Multi-customer org → use the customer + scoped-site picker. For
  // single-customer orgs the existing flat site picker is still the
  // simplest UX.
  const isMultiCustomer = customers.length > 1

  // Sites grouped by customer for the nested picker. Sites missing
  // a customer_id (legacy data) fall under an "Unassigned" bucket so
  // they remain pickable while admins migrate them.
  const sitesByCustomer = sites.reduce<Record<string, Site[]>>((acc, s) => {
    const key = s.customer_id ?? '__unassigned'
    ;(acc[key] ??= []).push(s)
    return acc
  }, {})

  function toggleCustomer(id: string) {
    setCustomerIds((prev) => {
      if (prev.includes(id)) {
        // Also drop any sites under this customer when un-checking it.
        const dropped = (sitesByCustomer[id] ?? []).map((s) => s.id)
        setSiteIds((cur) => cur.filter((s) => !dropped.includes(s)))
        return prev.filter((c) => c !== id)
      }
      return [...prev, id]
    })
  }

  function toggleSite(id: string) {
    setSiteIds((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id])
  }

  function toggleAll() {
    if (allSites) { setAllSites(false); setSiteIds([]) }
    else { setAllSites(true); setSiteIds(sites.map((s) => s.id)) }
  }

  const inviteM = useMutation({
    mutationFn: () =>
      api.invitations.create({
        email: email.trim(),
        full_name: fullName.trim(),
        role_id: roleId || undefined,
        customer_ids: isAdmin
          ? undefined
          : isMultiCustomer && customerIds.length > 0
            ? customerIds
            : undefined,
        // customer_admin manages every site under its customers, so
        // don't send site_ids for that role.
        site_ids: isAdmin || isCustomerAdmin
          ? undefined
          : siteIds.length > 0 ? siteIds : undefined,
        is_client: isClient,
        is_vendor: isVendor,
      }),
    onSuccess: () => {
      toast.success(`Invitation sent to ${email}`)
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['invitations'] })
      setEmail(''); setFullName(''); setRoleId(''); setSiteIds([]); setAllSites(false); setCustomerIds([]); setIsClient(false); setIsVendor(false)
      onClose()
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Failed to send invitation'),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Invite a teammate</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2.5 }}>
          They will receive an email with a link to join this workspace.
        </Typography>
        <Stack spacing={2}>
          <TextField label="Full name" required value={fullName} onChange={(e) => setFullName(e.target.value)}
            size="small" fullWidth autoFocus placeholder="e.g. John Smith"
            helperText="The person's display name in the system"
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
          <Tooltip title="They'll receive an invite email at this address" arrow placement="top-start">
            <TextField label="Email address" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              size="small" fullWidth placeholder="john@example.com"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
          </Tooltip>
          <Tooltip title="Determines what this person can see and do in the system" arrow placement="top-start">
            <Box>
              <SearchableSelect
                value={roleId}
                onChange={setRoleId}
                options={roles}
                getValue={(r) => r.id}
                getLabel={(r) => `${r.name}${r.is_system ? ' (system)' : ''}`}
                placeholder="Select a role *"
              />
            </Box>
          </Tooltip>

          {/* Display-only client tag — see CreateUserDialog for rationale. */}
          <FormControlLabel
            control={<Checkbox size="small" checked={isClient}
              onChange={(e) => setIsClient(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 13 }}>
                This user is a client
                <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.75 }}>
                  Shown under the Clients tab. Doesn't change what they can access.
                </Typography>
              </Typography>
            }
            sx={{ mx: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.25 } }}
          />

          {/* Display-only vendor tag — same semantics as the client tag. */}
          <FormControlLabel
            control={<Checkbox size="small" checked={isVendor}
              onChange={(e) => setIsVendor(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 13 }}>
                This user is a vendor
                <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.75 }}>
                  Shown under the Vendors tab. Doesn't change what they can access.
                </Typography>
              </Typography>
            }
            sx={{ mx: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.25 } }}
          />

          {/* ── Multi-customer org: customer + scoped-site picker ── */}
          {roleId && !isAdmin && isMultiCustomer && (() => {
            // See CreateUserDialog for the same filter shape — kept
            // duplicated (not extracted) because the invite and
            // create flows have subtly different surrounding state
            // and any wrapper would need to thread every callback.
            const q = scopeQuery.trim().toLowerCase()
            const filteredCustomers = q === ''
              ? customers
              : customers.filter((c) => {
                  if (c.name.toLowerCase().includes(q)) return true
                  return (sitesByCustomer[c.id] ?? [])
                    .some((s) => s.name.toLowerCase().includes(q))
                })
            return (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <BusinessOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  {isCustomerAdmin ? 'Customers they can manage' : 'Customer + site access'}
                </Typography>
              </Stack>
              <TextField
                size="small" fullWidth
                value={scopeQuery}
                onChange={(e) => setScopeQuery(e.target.value)}
                placeholder="Search customers or sites"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, maxHeight: 280, overflowY: 'auto' }}>
                {filteredCustomers.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No customers or sites match your search.
                  </Typography>
                )}
                {filteredCustomers.map((c, ci) => {
                  const checked = customerIds.includes(c.id)
                  const customerSites = sitesByCustomer[c.id] ?? []
                  // When only the site name matched, narrow the
                  // rendered site rows so the eye lands on the hit
                  // instead of scanning every site under the customer.
                  const siteQueryMatchesCustomer = q === '' || c.name.toLowerCase().includes(q)
                  const visibleCustomerSites = siteQueryMatchesCustomer
                    ? customerSites
                    : customerSites.filter((s) => s.name.toLowerCase().includes(q))
                  return (
                    <Box key={c.id} sx={{
                      borderTop: ci === 0 ? 'none' : '1px solid',
                      borderColor: 'divider',
                    }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                        bgcolor: checked
                          ? (t) => `${t.palette.primary.main}08`
                          : (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
                      }}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={checked} onChange={() => toggleCustomer(c.id)} sx={{ py: 0.25 }} />}
                          label={
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{c.name}</Typography>
                              {typeof c.site_count === 'number' && (
                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                  {c.site_count} site{c.site_count === 1 ? '' : 's'}
                                </Typography>
                              )}
                            </Stack>
                          }
                          sx={{ m: 0, flex: 1 }}
                        />
                      </Box>
                      {/* Site rows only shown for technician/viewer; the
                          customer is checked. customer_admin manages
                          every site, so we hide it for them. */}
                      {checked && !isCustomerAdmin && visibleCustomerSites.length > 0 && (() => {
                        // Per-customer select-all — bulk-picks every
                        // currently-visible site (search-narrowed if
                        // active). Mirrors CreateUserDialog exactly.
                        const visIds = visibleCustomerSites.map((s) => s.id)
                        const pickedFromVisible = visIds.filter((id) => siteIds.includes(id)).length
                        const allVisiblePicked = pickedFromVisible === visIds.length
                        const label = q === ''
                          ? `Select all ${visIds.length}`
                          : `Select all ${visIds.length} matching`
                        return (
                          <Stack sx={{ pl: 4, py: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                              <Button size="small"
                                onClick={() => setSiteIds((prev) => {
                                  if (allVisiblePicked) {
                                    const drop = new Set(visIds)
                                    return prev.filter((id) => !drop.has(id))
                                  }
                                  return Array.from(new Set([...prev, ...visIds]))
                                })}
                                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: 'primary.main', minWidth: 0, py: 0.25 }}>
                                {allVisiblePicked ? 'Deselect all' : label}
                              </Button>
                              <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                                {pickedFromVisible} / {visIds.length}
                              </Typography>
                            </Stack>
                            {visibleCustomerSites.map((s) => (
                              <FormControlLabel key={s.id}
                                control={<Checkbox size="small" checked={siteIds.includes(s.id)} onChange={() => toggleSite(s.id)} sx={{ py: 0.25 }} />}
                                label={<Typography sx={{ fontSize: 12.5 }}>{s.name}</Typography>}
                                sx={{ pl: 0.5, m: 0 }}
                              />
                            ))}
                          </Stack>
                        )
                      })()}
                      {checked && !isCustomerAdmin && customerSites.length === 0 && (
                        <Typography sx={{ pl: 4, py: 0.75, fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic' }}>
                          No sites yet under this customer.
                        </Typography>
                      )}
                    </Box>
                  )
                })}
              </Box>
              {customerIds.length === 0 && (
                <Typography sx={{ fontSize: 11, color: '#D97706', mt: 0.75 }}>
                  Pick at least one customer — this person can't see anything otherwise.
                </Typography>
              )}
              {!isCustomerAdmin && customerIds.length > 0 && siteIds.length === 0 && (
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.75 }}>
                  No sites picked — they'll see customer-level data but no site-specific lists until granted access.
                </Typography>
              )}
            </Box>
            )
          })()}

          {/* ── Single-customer org: original flat site picker ── */}
          {roleId && !isAdmin && !isMultiCustomer && sites.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  Site access
                </Typography>
                <Tooltip title="Choose which sites this person can access. Admins automatically get access to all sites." arrow placement="top-start">
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', cursor: 'help' }}>?</Typography>
                </Tooltip>
              </Stack>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={allSites} onChange={toggleAll} sx={{ py: 0.25 }} />}
                    label={<Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>All sites</Typography>}
                  />
                </Box>
                <Stack sx={{ maxHeight: 160, overflowY: 'auto', scrollbarWidth: 'thin' }}>
                  {sites.map((s) => (
                    <FormControlLabel key={s.id}
                      control={<Checkbox size="small" checked={siteIds.includes(s.id)} onChange={() => toggleSite(s.id)} sx={{ py: 0.25 }} />}
                      label={<Typography sx={{ fontSize: 12.5 }}>{s.name}</Typography>}
                      sx={{ px: 1.5, py: 0.5, m: 0, borderBottom: '1px solid', borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
              {siteIds.length === 0 && (
                <Typography sx={{ fontSize: 11, color: '#D97706', mt: 0.75 }}>
                  No sites selected — this person won't see any site-specific data until access is granted.
                </Typography>
              )}
            </Box>
          )}
          {isAdmin && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1, py: 0.75, borderRadius: 1, bgcolor: (t) => `${t.palette.primary.main}08` }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography sx={{ fontSize: 12, color: 'primary.main', fontWeight: 500 }}>Admins automatically have access to all sites and customers.</Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => inviteM.mutate()}
          disabled={
            !email.trim() || !fullName.trim() || !roleId || inviteM.isPending
            // Multi-customer org + non-admin role: must pick at least
            // one customer. Mirrors the server-side guard.
            || (!isAdmin && isMultiCustomer && customerIds.length === 0)
          }
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {inviteM.isPending ? 'Sending...' : 'Send invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Filter chip ───────────────────────────────────────────────────────────

function FilterChip({
  icon, label, active, value, onChange, options,
}: {
  icon: React.ReactNode
  label: string
  active: boolean
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <Select
      value={value} onChange={(e) => onChange(e.target.value)}
      size="small" variant="standard" disableUnderline
      renderValue={() => (
        <Stack direction="row" alignItems="center" spacing={0.85} sx={{ px: 0.5 }}>
          <Box sx={{ color: active ? 'primary.main' : 'text.secondary', display: 'inline-flex' }}>{icon}</Box>
          <Box component="span"
            sx={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '-0.005em', color: active ? 'text.primary' : 'text.secondary' }}
          >{label}</Box>
        </Stack>
      )}
      MenuProps={{ PaperProps: { sx: { minWidth: 200, borderRadius: 1.5, mt: 0.5 } } }}
      sx={{
        height: 32, borderRadius: 2,
        border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
        bgcolor: 'background.paper', transition: 'border-color 140ms ease',
        '& .MuiSelect-select': { py: 0, pr: '28px !important', pl: 1.25 },
        '& .MuiSelect-icon': { color: 'text.secondary', fontSize: 18, right: 6 },
        '&:hover': { borderColor: active ? 'primary.main' : 'text.secondary' },
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>
      ))}
    </Select>
  )
}

function formatRole(role: string): string {
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
